<template>
  <div class="ocr-page-shimmer" :class="sectionClass">
    <template v-if="variant === 'dashboard'">
      <header class="ocr-dashboard-header">
        <div class="ocr-shimmer-header-text">
          <OcrSkLine width="140px" height="22px" />
          <OcrSkLine width="100%" max-width="480px" height="12px" class="ocr-sk-mt-sm" />
        </div>
        <OcrSkLine width="76px" height="32px" pill class="ocr-sk-refresh" />
      </header>
      <div class="ocr-shimmer-tabs">
        <OcrSkLine v-for="n in 4" :key="'dt-' + n" width="92px" height="32px" pill />
      </div>
      <div class="ocr-summary-cards">
        <div v-for="n in 4" :key="'card-' + n" class="ocr-summary-card">
          <OcrSkLine width="56px" height="26px" />
          <OcrSkLine width="85%" height="13px" class="ocr-sk-card-label" />
        </div>
      </div>
      <div class="ocr-dash-charts-row">
        <div class="ocr-dash-chart-panel">
          <OcrSkLine width="128px" height="14px" />
          <OcrSkLine width="160px" height="11px" class="ocr-sk-mt-xs" />
          <OcrSkBlock height="240px" class="ocr-sk-mt-md" />
        </div>
        <div class="ocr-dash-chart-panel">
          <OcrSkLine width="108px" height="14px" />
          <OcrSkBlock height="240px" class="ocr-sk-mt-md" />
        </div>
      </div>
      <div class="ocr-dash-charts-row ocr-dash-charts-row--activity">
        <div class="ocr-dash-chart-panel ocr-dash-chart-panel--activity">
          <OcrSkLine width="120px" height="14px" />
          <OcrSkLine width="200px" height="11px" class="ocr-sk-mt-xs" />
          <OcrTableSectionSkeleton embedded :columns="tableColsRecent" :row-count="5" />
        </div>
        <div class="ocr-dash-chart-panel">
          <OcrSkLine width="124px" height="14px" />
          <OcrSkBlock height="8px" round class="ocr-sk-mt-md ocr-sk-memory-bar" />
          <OcrSkLine width="90%" height="11px" class="ocr-sk-mt-sm" />
          <div class="ocr-summary-card ocr-summary-card--skeleton ocr-sk-mt-md">
            <OcrSkLine width="48px" height="22px" />
            <OcrSkLine width="70%" height="12px" />
          </div>
        </div>
      </div>
    </template>

    <template v-else-if="variant === 'attributes'">
      <div class="ocr-panel ocr-panel--skeleton">
        <OcrSkLine width="100%" height="40px" round />
      </div>
      <div class="ocr-summary-cards">
        <div v-for="n in 4" :key="'attr-' + n" class="ocr-summary-card">
          <OcrSkLine width="40px" height="22px" />
          <OcrSkLine width="75%" height="12px" />
        </div>
      </div>
      <div class="ocr-dash-attributes-grid">
        <OcrTableSectionSkeleton title="Report details" :columns="['Field', 'Value']" :row-count="6" />
        <OcrTableSectionSkeleton title="Extracted tables" :columns="['#', 'Page', 'Type', 'Rows']" :row-count="4" />
      </div>
    </template>

    <template v-else-if="variant === 'upload'">
      <div class="ocr-shimmer-tabs">
        <OcrSkLine
          v-for="(w, i) in tabWidths"
          :key="'tab-' + i"
          :width="w + 'px'"
          height="32px"
          pill
        />
      </div>
      <div class="ocr-upload-panel ocr-upload-panel--skeleton">
        <div class="ocr-upload-panel-head">
          <OcrSkLine width="40px" height="40px" round />
          <div class="ocr-upload-panel-head-text">
            <OcrSkLine width="220px" height="16px" />
            <OcrSkLine width="92%" height="12px" max-width="480px" class="ocr-sk-mt-sm" />
          </div>
        </div>
        <OcrSkLine width="100%" height="88px" round />
      </div>
      <div class="ocr-file-panel ocr-file-panel--uploaded ocr-file-panel--skeleton">
        <div class="ocr-file-panel-header">
          <OcrSkLine width="120px" height="16px" />
          <OcrSkLine width="32px" height="26px" pill />
        </div>
        <div class="ocr-excel-grid-loading">
          <div v-for="n in 4" :key="'uf-' + n" class="ocr-excel-grid-card ocr-excel-grid-card--skeleton" />
        </div>
      </div>
    </template>

    <template v-else-if="variant === 'reports'">
      <div class="ocr-process-nav ocr-process-nav--shimmer">
        <OcrSkLine
          v-for="n in 4"
          :key="'rtab-' + n"
          width="78%"
          height="14px"
        />
      </div>
      <div class="ocr-panel">
        <OcrSkLine width="100%" max-width="480px" height="12px" />
        <OcrSkLine width="100%" height="48px" max-width="420px" round class="ocr-sk-mt-md" />
      </div>
      <OcrTableSectionSkeleton title="Reports" :columns="['Report', 'Status']" :row-count="4" />
    </template>

    <template v-else-if="variant === 'configuration'">
      <div class="ocr-panel ocr-panel--intro">
        <OcrSkLine width="95%" height="12px" />
        <OcrSkLine width="70%" height="12px" class="ocr-sk-mt-sm" />
      </div>
      <div class="ocr-config-layout">
        <div class="ocr-config-nav ocr-config-nav--skeleton">
          <div
            v-for="n in 8"
            :key="'nav-' + n"
            class="ocr-shimmer-nav-item"
            :class="{ 'ocr-shimmer-nav-item--active': n === 1 }"
          >
            <OcrSkLine width="78%" height="14px" />
          </div>
        </div>
        <div class="ocr-config-editor">
          <div class="ocr-config-editor-head">
            <div>
              <OcrSkLine width="140px" height="16px" />
              <OcrSkLine width="72px" height="12px" />
            </div>
            <div class="ocr-config-actions ocr-config-actions--skeleton">
              <OcrSkLine width="72px" height="32px" pill />
              <OcrSkLine width="64px" height="32px" pill />
            </div>
          </div>
          <OcrTableSectionSkeleton
            embedded
            :columns="['Column A', 'Column B', 'Column C']"
            :row-count="6"
          />
        </div>
      </div>
    </template>

    <template v-else-if="variant === 'test-data'">
      <div class="ocr-shimmer-tabs">
        <OcrSkLine v-for="n in 3" :key="'td-tab-' + n" :width="tabWidths[n - 1] + 'px'" height="28px" pill />
      </div>
      <div class="ocr-toolbar">
        <div class="ocr-toolbar-cluster">
          <OcrSkLine width="220px" height="36px" />
          <OcrSkLine width="88px" height="32px" pill />
          <OcrSkLine width="120px" height="32px" pill />
        </div>
      </div>
      <OcrTableSectionSkeleton title="Reports" :columns="['Report', 'Sample', 'CCE']" :row-count="6" />
    </template>

    <template v-else-if="variant === 'file-list'">
      <OcrTableSectionSkeleton :title="fileListTitle" :columns="['File name']" />
    </template>
  </div>
</template>

<script>
import OcrTableSectionSkeleton from "@/components/layout/OCR/OcrTableSectionSkeleton.vue";
import OcrSkLine from "@/components/layout/OCR/OcrSkLine.vue";
import OcrSkBlock from "@/components/layout/OCR/OcrSkBlock.vue";

export default {
  name: "OcrPageShimmer",
  components: { OcrTableSectionSkeleton, OcrSkLine, OcrSkBlock },
  props: {
    variant: {
      type: String,
      default: "dashboard",
      validator: (v) =>
        [
          "dashboard",
          "attributes",
          "upload",
          "reports",
          "configuration",
          "test-data",
          "file-list",
        ].includes(v),
    },
    fileListTitle: { type: String, default: "Uploaded files" },
  },
  data() {
    return {
      tabWidths: [128, 168, 112],
      tableColsRecent: ["File", "Status", "Pages", "Tables", "Confidence", "Modified"],
    };
  },
  computed: {
    sectionClass() {
      if (this.variant === "dashboard") return "ocr-dashboard-page";
      return null;
    },
  },
};
</script>
